import React, { lazy, Suspense, useEffect } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import AuthHandoffPage from "./products/AuthHandoffPage";
import GuestClaimBridge from "./products/GuestClaimBridge";
import GatewayPage from "./pages/GatewayPage";
import SpaceHomePage from "./pages/SpaceHomePage";
import TheDoARHome from "./pages/TheDoARHome";
import MarketplaceHome from "./pages/MarketplaceHome";
import RealityPage from "./pages/RealityPage";
import PublishMarketplacePage from "./pages/PublishMarketplacePage";
import WorldRoute from "./products/WorldRoute";
import {
  getSupersededProductDestination,
  isAuthUtilityPath,
  isDoarAiHostname,
  isGatewayHostname,
  isSpaceHostname,
  isMarketplaceHostname,
  isLegacyAiPath,
  isLegacyAppPath,
  isSpacePath,
  legacyAppToSpace,
} from "./products/productRouting";
import CookieNotice from "./components/billing/CookieNotice";
import GlobalAccountMenu from "./components/account/GlobalAccountMenu";
import { SurfaceLoader } from "./components/feedback/DoARAsync";
import "./components/Auth/Login/LoginBrand.css";
import "./App.css";

const AppRouter = lazy(() => import("./AppRouter"));
const AskApp = lazy(() => import("./products/AskApp"));
const SpaceApp = lazy(() => import("./products/SpaceApp"));

const IS_PROD = process.env.NODE_ENV === "production";
const AI_PRODUCT_URL = process.env.REACT_APP_AI_URL || "https://thedoar.ai";
const SPACE_PRODUCT_URL = process.env.REACT_APP_SPACE_URL || "https://doarspace.com";
const GATEWAY_PRODUCT_URL = process.env.REACT_APP_GATEWAY_URL || "https://todoar.co";
const COM_PRODUCT_URL = "https://www.thedoar.com";

function hasLegacyComGatewayJourney() {
  try {
    return Boolean(sessionStorage.getItem("doar_gateway_guest_token") && sessionStorage.getItem("doar_gateway_active_v2"));
  } catch {
    return false;
  }
}

function ProductLoader() {
  return (
    <SurfaceLoader
      title="DoAR"
      detail="Opening your workspace…"
      slowDetail="Still opening this surface. You can retry without changing your saved work."
      onRetry={() => window.location.reload()}
      homeHref="/"
    />
  );
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    if (!IS_PROD) console.error("[ErrorBoundary]", error, info.componentStack);
  }
  render() {
    if (this.state.hasError) {
      const worldLoadFailure = this.state.error?.code === "WORLD_MODULE_TIMEOUT" || /chunk|loading.*module|dynamically imported/i.test(String(this.state.error?.message || ""));
      return (
        <div style={{
          minHeight: "100vh", display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center",
          background: "#030c16", color: "#e2e8f0",
          fontFamily: "'SF Pro Display', Inter, system-ui, sans-serif",
          gap: 16, padding: 32, textAlign: "center",
        }}>
          <div style={{ fontSize: 28, fontWeight: 700 }}>{worldLoadFailure ? "Workspace needs a refresh" : "Something went wrong"}</div>
          <div style={{ color: "#94a3b8", fontSize: 14, maxWidth: 420, lineHeight: 1.6 }}>
            {worldLoadFailure ? "The latest DoARSpace interface did not finish loading. Refresh once to load the current release; your work is unchanged." : (IS_PROD ? "An unexpected error occurred. Refresh the page or return home." : (this.state.error?.message || "An unexpected error occurred."))}
          </div>
          <div style={{ display: "flex", gap: 10, marginTop: 8, flexWrap: "wrap", justifyContent: "center" }}>
            <button type="button" onClick={() => window.location.reload()} style={{ padding: "10px 22px", borderRadius: 8, background: "rgba(0,200,255,0.12)", border: "1px solid rgba(0,200,255,0.3)", color: "#00c8ff", cursor: "pointer", fontSize: 13, fontWeight: 600, fontFamily: "inherit" }}>Refresh page</button>
            <button type="button" onClick={() => { window.location.href = "/"; }} style={{ padding: "10px 22px", borderRadius: 8, background: "transparent", border: "1px solid rgba(255,255,255,0.12)", color: "rgba(255,255,255,0.55)", cursor: "pointer", fontSize: 13, fontWeight: 600, fontFamily: "inherit" }}>Go home</button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function ExternalRedirect({ to }) {
  useEffect(() => { window.location.replace(to); }, [to]);
  return <ProductLoader />;
}

function LegacyAppRedirect({ pathname, search, hash }) {
  const canonicalPath = legacyAppToSpace(pathname);
  return <Navigate to={`${canonicalPath}${search || ""}${hash || ""}`} replace />;
}

function ProductRoot() {
  const location = useLocation();
  const hostname = window.location.hostname;
  const aiHost = isDoarAiHostname(hostname);
  const explicitGatewayHost = isGatewayHostname(hostname);
  const explicitSpaceHost = isSpaceHostname(hostname);
  const explicitMarketplaceHost = isMarketplaceHostname(hostname);
  const hasExplicitProductHost = aiHost || explicitGatewayHost || explicitSpaceHost || explicitMarketplaceHost;
  const buildSurface = String(process.env.REACT_APP_PUBLIC_SURFACE || "").toLowerCase();
  const gatewayHost = explicitGatewayHost || (!hasExplicitProductHost && buildSurface === "gateway");
  const spaceHost = explicitSpaceHost || (!hasExplicitProductHost && buildSurface === "space");
  const marketplaceHost = explicitMarketplaceHost || (!hasExplicitProductHost && buildSurface === "marketplace");
  const pathname = location.pathname;
  const routeQuery = new URLSearchParams(location.search || "");
  const publicRealityAr = /^\/d\/[a-f0-9]{24}\/ar$/i.test(pathname) && (routeQuery.has("listing") || ["marketplace", "reality"].includes(routeQuery.get("from")));

  if (gatewayHost) {
    if (pathname === "/auth/complete") return <AuthHandoffPage mode="complete" />;
    if (pathname !== "/") return <Navigate to={`/${location.search || ""}${location.hash || ""}`} replace />;
    return <GatewayPage />;
  }

  if (spaceHost) {
    if (pathname === "/auth/complete") return <AuthHandoffPage mode="complete" />;

    if (pathname === "/") {
      const params = new URLSearchParams(location.search || "");
      const doarId = String(params.get("doar") || params.get("object") || "");
      if (/^[a-f0-9]{24}$/i.test(doarId)) {
        // Root query handoffs are compatibility entrances only. Normalize them
        // immediately to the one canonical object-centric Space route.
        params.set("doar", doarId);
        params.delete("object");
        const query = params.toString();
        return <Navigate to={`/d/${doarId}${query ? `?${query}` : ""}${location.hash || ""}`} replace />;
      }
      return <><SpaceHomePage /><CookieNotice /></>;
    }

    // /world/:projectId is a compatibility alias: the canonical workspace
    // runtime route is /space/:projectId (see SpaceApp.jsx's
    // WorkspaceRuntimeGate). Redirect once, preserving query/hash, rather
    // than mounting WorldApp a second time under a competing path. See
    // docs/doar-core/DOARSPACE_UNIVERSAL_WORKSPACE_CONTRACT.md #7-#8.
    const worldMatch = /^\/world\/([a-f0-9]{24})$/i.exec(pathname);
    if (worldMatch) {
      return <Navigate to={`/space/${worldMatch[1]}${location.search || ""}${location.hash || ""}`} replace />;
    }
    if (isLegacyAppPath(pathname)) return <LegacyAppRedirect pathname={pathname} search={location.search} hash={location.hash} />;
    if (isSpacePath(pathname)) return <><SpaceApp /><CookieNotice /></>;
    return <Navigate to={`/${location.search || ""}`} replace />;
  }

  if (aiHost) {
    if (pathname === "/login" || pathname === "/register") return <Navigate to="/" replace />;
    if (pathname === "/auth/complete") return <AuthHandoffPage mode="complete" />;
    if (isAuthUtilityPath(pathname)) return <ExternalRedirect to={`${COM_PRODUCT_URL}${pathname}${location.search || ""}`} />;
    if (pathname === "/" || /^\/c\/[a-f0-9]{24}$/i.test(pathname)) return <AskApp />;
    return <Navigate to="/" replace />;
  }

  if (marketplaceHost && pathname === "/create") {
    return <ExternalRedirect to={`${GATEWAY_PRODUCT_URL}/${location.search || ""}${location.hash || ""}`} />;
  }

  if (marketplaceHost && pathname === "/do") {
    // Migration-only recovery for a temporary guest DoAR created by the
    // superseded One-Origin release. New /do visits move to ToDoAR.co.
    if (hasLegacyComGatewayJourney()) return <GatewayPage />;
    return <ExternalRedirect to={`${GATEWAY_PRODUCT_URL}/${location.search || ""}${location.hash || ""}`} />;
  }
  if (marketplaceHost && pathname === "/ask") {
    return <ExternalRedirect to={`${AI_PRODUCT_URL}/${location.search || ""}${location.hash || ""}`} />;
  }
  if (marketplaceHost && /^\/c\/[a-f0-9]{24}$/i.test(pathname)) {
    return <ExternalRedirect to={`${AI_PRODUCT_URL}${pathname}${location.search || ""}${location.hash || ""}`} />;
  }
  if (marketplaceHost && publicRealityAr) {
    return <><SpaceApp /><CookieNotice /></>;
  }

  if (marketplaceHost && (
    pathname === "/"
    || pathname === "/marketplace"
    || pathname === "/publish"
    || pathname.startsWith("/r/")
    || pathname.startsWith("/m/")
  )) {
    return (
      <>
        <Routes>
          <Route path="/" element={<TheDoARHome />} />
          <Route path="/marketplace" element={<MarketplaceHome />} />
          <Route path="/r/:slug" element={<RealityPage />} />
          <Route path="/m/:slug" element={<RealityPage />} />
          <Route path="/publish" element={<PublishMarketplacePage />} />
        </Routes>
        <CookieNotice />
      </>
    );
  }

  if (marketplaceHost && isSpacePath(pathname)) {
    return <ExternalRedirect to={`${SPACE_PRODUCT_URL}${pathname}${location.search || ""}${location.hash || ""}`} />;
  }
  if (marketplaceHost && isLegacyAppPath(pathname)) {
    const canonicalPath = legacyAppToSpace(pathname);
    return <ExternalRedirect to={`${SPACE_PRODUCT_URL}${canonicalPath}${location.search || ""}${location.hash || ""}`} />;
  }

  if (pathname === "/auth/handoff") return <AuthHandoffPage mode="start" />;

  if (isSpacePath(pathname)) {
    return <><SpaceApp /><CookieNotice /></>;
  }

  if (isLegacyAppPath(pathname)) {
    return <LegacyAppRedirect pathname={pathname} search={location.search} hash={location.hash} />;
  }

  if (isLegacyAiPath(pathname)) return <ExternalRedirect to={AI_PRODUCT_URL} />;

  const supersededDestination = getSupersededProductDestination(pathname);
  if (supersededDestination) return <Navigate to={`${supersededDestination}${location.search || ""}${location.hash || ""}`} replace />;

  return <><AppRouter /><CookieNotice /></>;
}

export default function WrappedApp() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
          <GuestClaimBridge />
          <GlobalAccountMenu />
          <Suspense fallback={<ProductLoader />}>
            <ProductRoot />
          </Suspense>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export { ProductRoot, WorldRoute };
